import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Select, Popup, User, Tooltip } from '_atoms';
import { fetchWrapper } from 'helpers/helpers';
import { useDebounceEffect } from 'helpers/useDebounceEffect';
import { ReactComponent as OptionalApprover } from 'dist/icons/person.svg';
import { ReactComponent as PrimaryApprover } from 'dist/icons/person-filled.svg';
import { ReactComponent as Delete } from 'dist/icons/close.svg';
import type { JobApprover } from 'store/reducers/jobReducer';
import type { ResponsibleRecruiter } from 'store/reducers/candidateReducer';
import type { PaginatedResponse } from 'store/reducers/types';
import type { SelectOption } from '_atoms/Select';
import './style.scss';

interface RecruiterOption {
  uuid: string;
  full_name: string;
  profile: { avatar?: string | null };
}

interface ApproverListItem extends SelectOption {
  value: string;
  label: string;
  avatar?: string | null;
  optional?: boolean;
  is_default?: boolean;
  is_responsible?: boolean;
}

const getRecruiters = (body: Record<string, unknown>) =>
  fetchWrapper<PaginatedResponse<RecruiterOption>>({
    url: 'users/',
    body,
  }).catch(() => []);

interface PopupEditApproversProps {
  open: boolean;
  approvers: Array<JobApprover & { optional?: boolean }>;
  responsibleRecruiter?: ResponsibleRecruiter;
  requestOptions: Record<string, unknown>;
  onClose: () => void;
  onSave: (approvers: Array<{ user: string; optional?: boolean }>) => void;
}

export const PopupEditApprovers: React.FC<PopupEditApproversProps> = ({
  open,
  onClose,
  onSave,
  approvers,
  responsibleRecruiter,
  requestOptions,
}) => {
  const { t } = useTranslation();
  const [approversList, setApproversList] = useState<ApproverListItem[]>([]);
  const [options, setOptions] = useState<ApproverListItem[]>([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    setApproversList(
      approvers.map(approver => ({
        value: approver.user.uuid,
        label: approver.user.full_name,
        avatar: approver.user.profile?.avatar,
        optional: approver.optional,
        is_default: approver.is_default,
        is_responsible: approver.user.uuid === responsibleRecruiter?.uuid,
      }))
    );
  }, [open, responsibleRecruiter]);

  const onAddApprover = (value: ApproverListItem) => {
    setApproversList(values => [value, ...values]);
    setOptions([]);
  };

  const onSearch = (search = '') => {
    setLoading(true);
    getRecruiters({ ...requestOptions, search, exclude_uuid__in: approversList.map(el => el.value) }).then(resp => {
      const newApproversOptions = (resp as PaginatedResponse<RecruiterOption>).results.map(user => ({
        value: user.uuid,
        label: user.full_name,
        avatar: user.profile.avatar,
        optional: false,
        is_default: false,
      }));
      setOptions(newApproversOptions);
      setLoading(false);
    });
  };
  const debounceSearch = useDebounceEffect(onSearch, 500);

  const setOptional = (value: string) => () =>
    setApproversList(items => items.map(item => (item.value === value ? { ...item, optional: !item.optional } : item)));
  const onDeleteItem = (value: string) => () => setApproversList(items => items.filter(item => item.value !== value));
  return (
    <Popup
      open={open}
      title={t('translation:popup_edit_approvers.title')}
      cancelButtonProps={{
        children: t('translation:popup_edit_approvers.button_cancel'),
        onClick: onClose,
      }}
      okButtonProps={{
        children: t('translation:popup_edit_approvers.button_save'),
        onClick: () => onSave(approversList.map(el => ({ user: el.value, optional: el.optional }))),
      }}
      className="edit-approvers-popup mobile-full-size"
    >
      <Select
        placeholder={t('translation:popup_edit_approvers.placeholder_select_approver')}
        className="edit-approvers__input"
        value={null}
        onSearch={debounceSearch}
        options={options}
        filterOption={false}
        onChange={onAddApprover}
        loading={loading}
        onBlur={() => setOptions([])}
      />
      <div className="edit-approvers__list">
        {approversList.map(approver => (
          <div key={approver.value} className="approver">
            <div className="approver__info">
              <User
                avatar={approver.avatar}
                fullName={
                  <>
                    {approver.label}
                    {approver.optional && (
                      <span className="optional-tag">{t('translation:popup_edit_approvers.text_optional')}</span>
                    )}
                  </>
                }
              />
            </div>
            {!approver.is_responsible && (
              <div className="approver__extra">
                {approver.optional ? (
                  <Tooltip label={t('translation:popup_edit_approvers.tooltip_mark_as_required')} placement="topRight">
                    <OptionalApprover onClick={setOptional(approver.value)} />
                  </Tooltip>
                ) : (
                  <Tooltip label={t('translation:popup_edit_approvers.tooltip_mark_as_optional')} placement="topRight">
                    <PrimaryApprover onClick={setOptional(approver.value)} />
                  </Tooltip>
                )}
                <Tooltip label={t('translation:popup_edit_approvers.tooltip_remove')} placement="topRight">
                  <Delete onClick={onDeleteItem(approver.value)} />
                </Tooltip>
              </div>
            )}
          </div>
        ))}
      </div>
    </Popup>
  );
};
